import React, { useState } from 'react'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction'
import ListItemIcon from '@mui/material/ListItemIcon'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import { Badge, Chip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import IconTooltipButton from '../../../../../button/IconTooltipButton'

const PROVIDER_LABEL = {
  'acer-oars': 'ACER OARS (PAT)'
}

const STATUS_CHIP = {
  running: { label: 'Syncing…', color: 'warning' },
  ok: { label: 'Last sync OK', color: 'success' },
  error: { label: 'Last sync failed', color: 'error' }
}

const formatDate = (iso) => {
  if (!iso) return null
  return new Date(iso).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

const ThirdPartyAccessList = ({ list = [], isLoading, permissions = {}, onModal, onSync, onHistory }) => {
  const [syncingId, setSyncingId] = useState(null)

  const handleSync = async (item) => {
    setSyncingId(item._id)
    try {
      await onSync(item._id)
    } finally {
      setSyncingId(null)
    }
  }

  if (isLoading) {
    return (
      <List>
        {Array(3)
          .fill()
          .map((_, i) => (
            <ListItem key={i}>
              <ListItemIcon>
                <Skeleton variant='circular' width='2rem' height='2rem' />
              </ListItemIcon>
              <ListItemText
                primary={<Skeleton variant='text' width={300} />}
                secondary={<Skeleton variant='text' width={200} />}
              />
            </ListItem>
          ))}
      </List>
    )
  }

  if (list.length === 0) {
    return (
      <Typography variant='inherit' component='p' className='text-muted-foreground opacity-50 py-2 px-2'>
        You don&apos;t have any external credential yet
      </Typography>
    )
  }

  return (
    <List>
      {list.map((item) => {
        const status = STATUS_CHIP[item.lastSyncStatus]
        const lastSync = formatDate(item.lastSyncAt)
        return (
          <ListItem key={item._id}>
            <ListItemIcon>
              <Badge
                color={item.enabled ? 'primary' : 'inherit'}
                badgeContent={item.enabled ? 'On' : 'Off'}
                variant='dot'
              >
                <FontAwesomeIcon className='text-2xl' icon={['fas', 'plug']} />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary={
                <span className='flex items-center gap-2'>
                  {item.label || PROVIDER_LABEL[item.provider] || item.provider}
                  {status && <Chip label={status.label} color={status.color} size='small' />}
                </span>
              }
              secondary={
                <>
                  {PROVIDER_LABEL[item.provider] || item.provider} · {item.schoolSlug} · {item.username}
                  {lastSync && ` · last sync: ${lastSync}`}
                  {item.lastSyncStatus === 'error' && item.lastSyncError && (
                    <span className='block text-red-500'>{item.lastSyncError}</span>
                  )}
                </>
              }
            />
            <ListItemSecondaryAction>
              {permissions['sync'] && (
                <IconTooltipButton
                  title='Sync now'
                  icon={syncingId === item._id ? 'circle-notch' : ['fas', 'sync']}
                  spin={syncingId === item._id}
                  disabled={!item.enabled || syncingId === item._id}
                  onClick={() => handleSync(item)}
                />
              )}
              {permissions['history'] && (
                <IconTooltipButton
                  title='Sync history'
                  icon={['fas', 'history']}
                  onClick={() => onHistory(item)}
                />
              )}
              {permissions['updated'] && (
                <IconTooltipButton
                  title='Edit'
                  icon={['fas', 'edit']}
                  onClick={() => onModal('edit', item)}
                />
              )}
              {permissions['deleted'] && (
                <IconTooltipButton
                  title='Delete'
                  icon={['fas', 'trash-alt']}
                  onClick={() => onModal('delete', item)}
                />
              )}
            </ListItemSecondaryAction>
          </ListItem>
        )
      })}
    </List>
  )
}

export default ThirdPartyAccessList
